import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../shared/AppDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export function BillingPage({ user, onUserUpdate }) {
  const toast = useToast();
  const [plans, setPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [searchParams] = useSearchParams();

  // Load plans and transactions
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, txRes] = await Promise.all([
        fetch(`${API_URL}/api/billing/plans`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API_URL}/api/billing/transactions`, { headers: getAuthHeaders() }).then(r => r.json())
      ]);
      
      if (plansRes.success) setPlans(plansRes.data || []);
      if (txRes.success) setTransactions(txRes.data || []);
    } catch (error) {

    }
    setLoading(false);
  }, []);

  // Check payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true') {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
    
    loadData();
  }, [searchParams, loadData]);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/billing/status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      
      if (data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setCheckingPayment(false);
        // Reload user data
        if (onUserUpdate) onUserUpdate();
        loadData();
        // Clear URL params
        window.history.replaceState({}, '', '/billing');
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {

      setCheckingPayment(false);
    }
  };

  // Handle plan selection
  const handleSelectPlan = async (planId) => {
    if (planId === 'free' || planId === user?.plan) return;
    
    try {
      const res = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });
      
      const data = await res.json();
      
      if (data.success && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Fehler beim Erstellen der Checkout-Session');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const currentPlanId = user?.plan || 'free';
  const creditsRemaining = (user?.credits || 60) - (user?.credits_used || 0);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Abrechnung & Tarife</h1>
        <p className="text-gray-500">Verwalten Sie Ihr Abonnement und Ihre Credits</p>
      </div>

      {/* Payment Success Message */}
      {paymentSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
          <div className="text-4xl mb-2">🎉</div>
          <h3 className="text-lg font-semibold text-green-800">Zahlung erfolgreich!</h3>
          <p className="text-green-600">Ihr Plan wurde aktualisiert. Vielen Dank!</p>
        </div>
      )}

      {/* Checking Payment */}
      {checkingPayment && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-2"></div>
          <p className="text-blue-600">Zahlung wird verarbeitet...</p>
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Ihr aktueller Tarif</h2>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-lg font-bold text-lg ${
              currentPlanId === 'free' ? 'bg-gray-100 text-gray-700' :
              currentPlanId === 'starter' ? 'bg-blue-100 text-blue-700' :
              currentPlanId === 'pro' ? 'bg-purple-100 text-purple-700' :
              'bg-green-100 text-green-700'
            }`}>
              {currentPlanId.toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-gray-800">
                {plans.find(p => p.id === currentPlanId)?.name || 'Free'}
              </p>
              <p className="text-sm text-gray-500">
                {user?.subscription_started ? `Seit ${new Date(user.subscription_started).toLocaleDateString('de-DE')}` : 'Kostenloser Tarif'}
              </p>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="text-2xl font-bold text-gray-800">
              {creditsRemaining} <span className="text-base font-normal text-gray-500">/ {user?.credits || 60} Min</span>
            </p>
            <p className="text-sm text-gray-500">Credits verfügbar</p>
            <div className="w-full sm:w-48 h-2 bg-gray-200 rounded-full mt-2">
              <div 
                className={`h-full rounded-full ${creditsRemaining <= 10 ? 'bg-red-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.max(0, (creditsRemaining / (user?.credits || 60)) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Verfügbare Tarife</h2>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                className={`bg-white rounded-xl shadow-sm border-2 p-6 relative ${
                  plan.popular ? 'border-purple-500' : 'border-gray-200'
                } ${currentPlanId === plan.id ? 'ring-2 ring-blue-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    BELIEBT
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-4xl font-bold text-gray-800">{plan.price}€</span>
                    {plan.price > 0 && <span className="text-gray-500">/Monat</span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{plan.credits} Minuten</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="text-green-500">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={plan.id === 'free' || plan.id === currentPlanId}
                  className={`w-full py-3 rounded-lg font-medium transition-all ${
                    plan.id === currentPlanId
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : plan.id === 'free'
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {plan.id === currentPlanId ? 'Aktueller Plan' : 
                   plan.id === 'free' ? 'Kostenlos' : 
                   'Jetzt upgraden'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Zahlungshistorie</h2>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Keine Zahlungen vorhanden
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Datum</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Plan</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Betrag</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map((tx, i) => (
                <tr key={i}>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(tx.created_at).toLocaleDateString('de-DE')}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                      {tx.plan_id?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-800">
                    {tx.amount}€
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      tx.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                      tx.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {tx.payment_status === 'completed' ? 'Bezahlt' :
                       tx.payment_status === 'pending' ? 'Ausstehend' : 'Fehlgeschlagen'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default BillingPage;
